import React from "react"
import { graphql, Link } from "gatsby"
import { useTable, useFilters, useSortBy } from "react-table"

import Layout from "../components/layout"
import slugify from "../helper/slugify"
import Seo from "../components/seo"

function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach(row => {
      options.add(row.values[id])
    })
    return [...options.values()]
  }, [id, preFilteredRows])

  // Render a multi-select box
  return (
    <select
      name={id}
      id={id}
      value={filterValue}
      onChange={e => {
        setFilter(e.target.value || undefined)
      }}
      className="block appearance-none w-full px-3 py-1.5
      text-gray-700 font-normal
      bg-white bg-clip-padding
      text-ellipsis overflow-hidden
      border border-solid border-darkcdfblue
      transition ease-in-out
      m-0
      focus:text-gray-700 focus:bg-white focus:border-cdfblue focus:outline-none
      shadow-solid-search"
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

const LinkTo = ({ value }) => {
  return (
    <Link
      to={"/" + value.slug + "#" + slugify(value.title)}
      className="text-cdfblue hover:underline"
    >
      {" "}
      {value.title}{" "}
    </Link>
  )
}

const AdvancedSearchPage = ({ data }) => {
  const allSpeaks = data.allVideosJson.edges
    .map(e =>
      e.node.speaks.map(s => ({
        session: e.node.title,
        slug: e.node.slug,
        linkTo: { slug: e.node.slug, title: s.title },
        day: e.node.day,
        ...s,
      }))
    )
    .flat()

  const memoSpeaks = React.useMemo(() => allSpeaks, [])

  const columns = React.useMemo(
    () => [
      {
        Header: "Session",
        accessor: "session",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Speaker",
        accessor: "speaker",
        Filter: SelectColumnFilter,
        filter: "includes",
      },
      {
        Header: "Speak",
        accessor: "linkTo",
        Cell: LinkTo,
      },
    ],
    []
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: memoSpeaks,
      },
      useFilters,
      useSortBy
    )

  return (
    <Layout>
      <Seo title="Advanced Search" />
      <header
        className="bg-orange-50 flex flex-row-reverse text-cdfblue font-mono
      border-b border-cdfblue
      p-4 justify-between"
      >
        <Link to="/" className="m-0 text-2xl mr-10">
          ✖
        </Link>
        <Link to="/" className="m-0 hidden md:block">
          /The Economics of Creative Destruction
        </Link>
      </header>
      <div className="container m-auto font-sans">
        <h2 className="text-darkcdfblue text-center p-5">Advanced Search</h2>
        <table {...getTableProps()} border="1" className="table-fixed">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  // Add the sorting props to control sorting. For this example
                  // we can add them into the header props
                  <th className="align-top">
                    <div
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " ▼"
                            : " ▲"
                          : ""}
                      </span>
                    </div>
                    {/* Add a sort direction indicator */}
                    <div className="flex">
                      {column.Filter ? column.render("Filter") : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()} className="odd:bg-orange-50">
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Layout>
  )
}

export default AdvancedSearchPage

export const query = graphql`
  query AdvancedSearchQuery {
    allVideosJson {
      edges {
        node {
          slug
          tags
          title
          speaks {
            speaker
            title
          }
        }
      }
    }
  }
`
